class API {
  constructor(options) {
    const defaults = {
      env: 'production'
    };

    this.config = {
      ...defaults,
      ...options
    };

    this._cancelations = {};
  }

  reset() {
    this._token = '';
    this._refreshToken = '';
    this._cancelations = {};
  }

  /**
   * Creates an API to abort requests.
   * Provide a `cancelToken` if the request candidate to be aborted is created using the `XMLHttpRequest (XHR)` object.
   * Otherwise for requests created using the `Fetch` API, do not provide `cancelToken` argument.
   * See https://developer.mozilla.org/en-US/docs/Web/API/AbortController for more details on how to abort requests using the `AbortController` interface.
   *
   * @param {String} [cancelToken] A unique token to identify which request to abort.
   * @return {Object} The appropriate API to use on each case for aborting a request.
   */
  abortController(cancelToken) {
    let returnable;

    if (cancelToken) {
      returnable = {
        abort: () => {
          if (this._cancelations[cancelToken]) {
            this._cancelations[cancelToken]();
            this._cancelations[cancelToken] = null;
            delete this._cancelations[cancelToken];
          }
        }
      };
    } else {
      try {
        returnable = new AbortController();
      } catch (err) {
        // If `AbortController` is not supported, return similar API so that it fails silently.
        returnable = {
          abort: () => {},
          signal: null
        };
      }
    }

    return returnable;
  }
}

/**
 * Public API methods
 */
const publicMethods = {
  // NDEs
  fetchNdes: require('./public/ndes/fetchNdes'),
  fetchNde: require('./public/ndes/fetchNde'),
  fetchNdeByUuid: require('./public/ndes/fetchNdeByUuid'),
  fetchStatistics: require('./public/ndes/fetchStatistics'),
  createNde: require('./public/ndes/createNde'),
  updateNde: require('./public/ndes/updateNde'),
  deleteNde: require('./public/ndes/deleteNde'),
  verifyRecaptcha: require('./public/ndes/verifyRecaptcha'),

  // Blog
  fetchBlogPosts: require('./public/blog/fetchBlogPosts'),
  fetchBlogPost: require('./public/blog/fetchBlogPost'),
  fetchBlogAuthors: require('./public/blog/fetchBlogAuthors'),
  fetchBlogAuthor: require('./public/blog/fetchBlogAuthor'),
  fetchBlogTags: require('./public/blog/fetchBlogTags'),
  blogLogin: require('./public/blog/blogLogin'),
  fetchMyBlogPosts: require('./public/blog/fetchMyBlogPosts'),
  fetchMyBlogProfile: require('./public/blog/fetchMyBlogProfile'),
  createBlogPost: require('./public/blog/createBlogPost'),
  updateBlogPost: require('./public/blog/updateBlogPost'),
  deleteBlogPost: require('./public/blog/deleteBlogPost'),
  uploadBlogImage: require('./public/blog/uploadBlogImage'),
  updateBlogAuthorProfile: require('./public/blog/updateBlogAuthorProfile'),
  subscribeNewsletter: require('./public/blog/subscribeNewsletter')
};

const api = new API();

Object.entries(publicMethods).forEach(([key, value]) => API.prototype[key] = (...args) => value.default(...args)(api));

export default api;
